/**
 * Web-side photo upload — mirror of `mobile/src/sync/syncPhotos.ts#sendPhotoUpsert`.
 *
 * Wire format (must match mobile byte-for-byte so a mobile peer
 * accepts what the web produces and vice-versa):
 *
 *   WS metadata broadcast:
 *     AES-CBC-256(teamKey, iv, JSON.stringify({
 *       type: 'photo_upsert',
 *       photo: { id, workId, localPath: '', thumbnail, uploadedAt,
 *                technicianId },
 *       timestamp: <epoch ms>,
 *     }))
 *     → relay.send → WS { type: 'sync', companyId, data: ivHex:base64(ct) }
 *
 *   HTTP encrypted binary (POST /photos/:id with x-company-token):
 *     AES-CBC-256(teamKey, iv, base64(<jpeg-bytes>))
 *     wire: ivHex:base64(ct)
 *
 * The relay is blind to the contents; both blobs are opaque on the
 * server side. Conflict resolution on the receiver follows the same
 * last-write-wins rule that already exists for Work upserts.
 *
 * Why mirror instead of reuse a shared encrypt helper:
 *   `syncWork.ts` exposes `__testing.encryptSyncBlob` but not as a
 *   public API. Re-deriving the few lines that produce the wire
 *   shape here keeps that internal seam internal and lets the photo
 *   pipeline grow its own retries / progress hooks without coupling
 *   to the work-sync test surface.
 */

import { aesCbcEncrypt, bytesToHex, hexToBytes, utf8ToBytes } from '../crypto/webCrypto';
import { savePhoto } from '../store/db';
import { relay } from './relay';
import { RELAY_HTTP_URL } from '../config/env';
import {
  dataUrlToBase64,
  dataUrlToBytes,
  resizeImage,
  validateMagicBytes,
  validateSize,
} from '../utils/imageProcessing';
import type { WebIdentity } from '../store/keys';
import type { Photo } from '../types';

/** 800px / 0.85 for the full-size JPEG; matches mobile's PDF-export
 *  resize so a photo taken on either platform appears the same size
 *  in any later document. */
const FULL_MAX_WIDTH = 800;
const FULL_QUALITY = 0.85;

/** 180px / 0.6 for the thumbnail; matches the mobile thumbnail used
 *  in the photo grid. Inline-stored on the Photo row so peers can
 *  preview before the full binary downloads. */
const THUMB_MAX_WIDTH = 180;
const THUMB_QUALITY = 0.6;

/** Wire format helper: encrypt arbitrary bytes with the team key
 *  and produce the `ivHex:base64(ct)` string mobile expects. */
async function encryptForWire(plaintext: Uint8Array, teamKeyHex: string): Promise<string> {
  const teamKey = hexToBytes(teamKeyHex);
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(16));
  const ct = await aesCbcEncrypt(teamKey, plaintext, iv);
  // Build base64 the same way `web/src/sync/syncWork.ts` does — go
  // through binary string + `btoa` so the output matches mobile's
  // `Buffer.from(...).toString('base64')` exactly (no URL-safe
  // variant, no missing padding).
  let bin = '';
  for (let i = 0; i < ct.length; i++) bin += String.fromCharCode(ct[i]);
  return `${bytesToHex(iv)}:${btoa(bin)}`;
}

/** Build the encrypted body for the binary POST. Mobile's
 *  `encryptFile` reads the file as a base64 string and encrypts THAT
 *  string; the web does the same so the peer that downloads + decrypts
 *  ends up with the same intermediate representation. */
async function encryptBase64ForBinary(jpegBase64: string, teamKeyHex: string): Promise<string> {
  return encryptForWire(utf8ToBytes(jpegBase64), teamKeyHex);
}

/**
 * Public — pick a randomly-stamped photo id. Exposed for tests + for
 * call sites that need to render the row's id in the UI before the
 * upload finishes (e.g. for an optimistic preview key). The default
 * `crypto.randomUUID` shape (lowercase v4) matches what mobile mints.
 */
export function newPhotoId(): string {
  // crypto.randomUUID is universal in evergreen browsers and Node 19+;
  // fall back to a manual v4 build only if a test environment stubs
  // it out.
  if (typeof globalThis.crypto.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  const b = globalThis.crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = bytesToHex(b);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export interface UploadPhotoResult {
  /** The persisted Photo row, with `localPath` set to a fresh
   *  `blob:` URL so the WorkDetail re-render shows the upload
   *  immediately. */
  photo: Photo;
}

export interface UploadPhotoOptions {
  /** Optional id override — useful for deterministic tests; defaults
   *  to `newPhotoId()`. */
  id?: string;
}

/**
 * Validate, resize, encrypt, broadcast metadata, and POST the
 * encrypted binary for a single File. Resolves once the HTTP POST
 * completes (so peers without a live socket can fetch the binary
 * via the next poll). Throws when validation fails or the upload
 * roundtrip errors — the caller surfaces the error inline.
 *
 * Order matters: we save locally + emit the WS broadcast BEFORE
 * the binary upload. That way a peer with an open socket sees the
 * thumbnail in milliseconds (good UX even if the binary is still
 * uploading) and the WorkDetail on this browser re-renders the new
 * row optimistically. If the binary POST fails, the savePhoto row
 * stays — there's no good way to "rollback" a broadcast that other
 * peers already received. We accept the cost: the photo grid will
 * show a thumbnail with a Blob URL pointing to the still-good local
 * decode, and the next sync round can retry the binary if needed.
 */
export async function sendPhotoUpsert(
  file: File,
  workId: string,
  identity: WebIdentity,
  opts: UploadPhotoOptions = {},
): Promise<UploadPhotoResult> {
  // 1. Validation — magic bytes first (cheap, fails on garbage),
  //    then size (so we don't spend CPU on a 50 MB file).
  await validateMagicBytes(file);
  validateSize(file);

  // 2. Resize: full + thumbnail. Sequential to keep main-thread
  //    pressure predictable; both share the same HTMLImageElement
  //    decode behind the scenes per call so the second call is fast.
  const full = await resizeImage(file, FULL_MAX_WIDTH, FULL_QUALITY);
  const thumb = await resizeImage(file, THUMB_MAX_WIDTH, THUMB_QUALITY);

  // 3. Mint the local Photo row + a Blob URL for the just-resized
  //    full JPEG so the WorkDetail can render the photo immediately
  //    without waiting on the binary download path. Peers will mint
  //    their own Blob URL after `applyPhotoMessage` decrypts.
  const photoId = opts.id ?? newPhotoId();
  const fullBytes = dataUrlToBytes(full.dataUrl);
  const localBlob = new Blob([fullBytes as BlobPart], { type: 'image/jpeg' });
  const localPath = URL.createObjectURL(localBlob);

  const photo: Photo = {
    id: photoId,
    workId,
    localPath,
    thumbnail: thumb.dataUrl,
    uploadedAt: Date.now(),
    technicianId: identity.technicianId,
  };

  // 4. Persist + broadcast metadata + upload binary. The order is
  //    deliberately persist→broadcast→upload (see header comment).
  try {
    await savePhoto(photo);

    // Wire-shape the broadcast: mobile peers store their own
    // localPath, so we strip ours from the wire payload to avoid
    // confusing them with a `blob:...` URL that means nothing
    // outside this browser session.
    const wirePhoto: Photo = { ...photo, localPath: '' };
    const message = JSON.stringify({
      type: 'photo_upsert',
      photo: wirePhoto,
      timestamp: Date.now(),
    });
    const encryptedMsg = await encryptForWire(utf8ToBytes(message), identity.teamKey);
    relay.send(encryptedMsg);

    // 5. Encrypted binary upload — with retry for flaky networks.
    //    Mobile peers without a live socket fetch the binary on the
    //    next /pending poll, so a single failed POST means the whole
    //    upload is "metadata-only" until the user retries. We retry
    //    up to 3 times with exponential backoff (1s/2s/4s) so a
    //    transient hiccup (Wi-Fi drop, captive-portal redirect)
    //    doesn't cost the user the photo.
    const fullBase64 = dataUrlToBase64(full.dataUrl);
    const encryptedBinary = await encryptBase64ForBinary(fullBase64, identity.teamKey);
    await postPhotoBinaryWithRetry(photoId, encryptedBinary, identity);

    return { photo };
  } catch (err) {
    // Best-effort cleanup of the blob URL we minted; on a failure the
    // caller will surface the error and the WorkDetail won't render
    // the row (savePhoto may or may not have completed). Revoking is
    // safe even if the URL was never used.
    URL.revokeObjectURL(localPath);
    throw err;
  }
}

/** Backoff schedule for the binary POST: ~7s total worst-case
 *  (1+2+4) before we surface a hard error. Mobile uses the same
 *  ladder for `downloadPhotoBinary`; matching the magnitudes keeps
 *  the cross-platform retry behaviour predictable. */
const RETRY_DELAYS_MS = [1_000, 2_000, 4_000];

/**
 * POST the encrypted binary with up to 3 retries. Throws once the
 * ladder is exhausted so the dropzone can surface the failure inline
 * (the savePhoto + WS broadcast already ran, so peers see the
 * thumbnail; the binary itself is the only piece missing).
 *
 * Test seam: the `fetcher` arg defaults to the global `fetch` so
 * unit tests can stub the network without unstubbing globals.
 */
export async function postPhotoBinaryWithRetry(
  photoId: string,
  body: string,
  identity: WebIdentity,
  fetcher: typeof fetch = fetch,
  delays: readonly number[] = RETRY_DELAYS_MS,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'text/plain',
    'x-company-token': identity.companyToken,
    'x-worker-id': identity.technicianId,
  };
  const url = `${RELAY_HTTP_URL}/photos/${photoId}`;

  let lastError: Error | null = null;
  // attempts = delays.length + 1 (one initial try + N retries)
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    try {
      const res = await fetcher(url, { method: 'POST', headers, body });
      if (res.ok) return;
      // 4xx (other than 408 Request Timeout) is a hard error: retry
      // wouldn't change the outcome, fail fast.
      if (res.status >= 400 && res.status < 500 && res.status !== 408) {
        throw new Error(`Photo upload failed: HTTP ${res.status}`);
      }
      lastError = new Error(`Photo upload failed: HTTP ${res.status}`);
    } catch (err) {
      // Re-throw client-side terminal errors so the loop doesn't
      // burn the full ladder on a permanent failure.
      if (err instanceof Error && /HTTP 4\d\d/.test(err.message) && !/HTTP 408/.test(err.message)) {
        throw err;
      }
      lastError = err instanceof Error ? err : new Error(String(err));
    }
    if (attempt < delays.length) {
      await sleep(delays[attempt]);
    }
  }
  throw lastError ?? new Error('Photo upload failed after retries');
}

/** Test seam — exposed only so unit tests can verify the wire shape
 *  without spinning the whole pipeline. */
export const __testing = { encryptForWire, encryptBase64ForBinary };
